import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Row,
  Col,
  Input,
  DatePicker,
  Select,
  Table,
  Tag,
  Space,
  Button,
  MenuProps,
  Dropdown,
  Pagination,
  Form,
  SelectProps,
  Modal,
  Divider,
  Typography,
  Tooltip,
} from "antd";
import {
  SearchOutlined,
  MoreOutlined,
  ScheduleOutlined,
  FilterOutlined,
  CheckCircleFilled,
  ExclamationCircleFilled,
} from "@ant-design/icons";
import BillingModal from "./BillingModal";
import { getChannelCost } from "../../constants/cost";
import type { ColumnsType } from "antd/es/table";
import dayjs, { Dayjs } from "dayjs";
import { ChannelEnum, StatusEnum, TypeEnum } from "../../constants/enums";
import { Campaign } from "../../models/campaign";
import { CHANNEL_OPTIONS, handleSelectAll } from "../../utils/options";
import {
  ChannelKeyMap,
  STATUS_OPTIONS,
  StatusKeyMap,
  TypeKeyMap,
} from "../../constants/labels";
import SegmentCell from "./SegmentCell";
import CampaignAlertModal from "./CampaignAlertModal";
import { useLocation, useNavigate } from "react-router-dom";
import { RoutePaths } from "../../routes/route";
import { useBrandStore } from "../../store/brandStore";
import { campaignService } from "../../services/campaignService";
import { brandRFMProfileService } from "../../services/brandRFMProfileService";
import { brandMemberSegmentService } from "../../services/brandMemberSegmentService";
import { Segment } from "../../models/segment";
import { useQueryNavigator } from "../../utils/navigate";
import { Variant } from "../../constants/type";
import CustomModal from "./Modal";

type ActionKey =
  | "view"
  | "edit"
  | "cancel"
  | "duplicate"
  // | "pause"
  // | "stop"
  // | "resume"
  | "delete"
  | "activate"
  | "pay";

const statusTag = (s: StatusEnum) => {
  const map: Record<StatusEnum, { color: string; label: string }> = {
    [StatusEnum.Draft]: {
      color: "default",
      label: StatusKeyMap[StatusEnum.Draft],
    },
    [StatusEnum.Active]: {
      color: "green",
      label: StatusKeyMap[StatusEnum.Active],
    },
    [StatusEnum.Paused]: {
      color: "orange",
      label: StatusKeyMap[StatusEnum.Paused],
    },
    [StatusEnum.Completed]: {
      color: "blue",
      label: StatusKeyMap[StatusEnum.Completed],
    },
    [StatusEnum.Cancelled]: {
      color: "red",
      label: StatusKeyMap[StatusEnum.Cancelled],
    },
  };

  const { color, label } = map[s];
  return <Tag color={color}>{label}</Tag>;
};
const channelIcon = (c: ChannelEnum) => {
  switch (c) {
    case ChannelEnum.Email:
      return <i className="icon-mail" />;
    case ChannelEnum.Sms:
      return <i className="icon-sms" />;
    case ChannelEnum.WhatsAppStandard:
      return <i className="icon-whatsapp" />;
    default:
      return <i className="icon-whatsapp" />;
  }
};
const isMobileOrTablet = window.innerWidth <= 1024;

const getActionMenuItems = (record: {
  Status: StatusEnum;
  Channel: number;
  ScheduleType: number;
  CampaignStart?: string;
  isBilled?: boolean;
}): MenuProps["items"] => {
  const items = (ACTION_ITEMS ?? []).filter(Boolean) as NonNullable<
    MenuProps["items"]
  >;

  if (record.Status === StatusEnum.Completed) {
    const allow = new Set(["view"]);
    if (record.isBilled === false) allow.add("pay");
    return items.filter((it) => allow.has(String((it as any).key)));
  }
  if (record.Status === StatusEnum.Cancelled) {
    const allow = new Set(["view", "delete"]);
    return items.filter((it) => allow.has(String((it as any).key)));
  }
  // if (record.ScheduleType !== TypeEnum.Onetime) {
  //   if (record.Status === StatusEnum.Draft) {
  //     const allow = new Set(["view", "edit", "delete", "activate"]);
  //     return items.filter((it) => allow.has(String((it as any).key)));
  //   }
  //   if (record.Status === StatusEnum.Paused) {
  //     const allow = new Set(["view", "resume"]);
  //     return items.filter((it) => allow.has(String((it as any).key)));
  //   }
  //   if (record.Status === StatusEnum.Active) {
  //     // already paid → only view
  //     if (record.isBilled) {
  //       return items.filter((it) => String((it as any).key) === "view");
  //     }

  //     // not paid → full actions
  //     const allow = new Set([
  //       "view",
  //       "edit",
  //       "cancel",
  //       "pay",
  //       "pause",
  //       "stop",
  //       "cancel",
  //     ]);
  //     return items.filter((it) => allow.has(String((it as any).key)));
  //   }
  // }

  if (
    record.Channel === ChannelEnum.WhatsAppStandard ||
    record.Channel === ChannelEnum.WhatsAppPremium ||
    record.Channel === ChannelEnum.Sms ||
    record.Channel === ChannelEnum.Email
    // && record.ScheduleType === TypeEnum.Onetime
  ) {
    const now = new Date();
    const campaignStart = record.CampaignStart
      ? new Date(record.CampaignStart)
      : null;
    const diffInHours = campaignStart
      ? (campaignStart.getTime() - now.getTime()) / (1000 * 60 * 60)
      : Infinity;

    if (record.Status === StatusEnum.Draft) {
      const allow = new Set(["view", "edit", "delete", "activate"]);
      let filtered = items.filter((it) => allow.has(String((it as any).key)));

      // Disable "activate" when diffInHours <= 1
      if (diffInHours <= 1) {
        filtered = filtered.map((it) => {
          const key = String((it as any).key);

          if (key === "activate") {
            return {
              ...(it as any),
              icon: false,
              disabled: true,
              label: (
                <Tooltip
                  placement="left"
                  title={
                    <div>
                      <strong>Activation is unavailable.</strong>
                      <div>
                        This campaign is currently locked due to its upcoming
                        send time. Please update the campaign details before
                        activating it.
                      </div>
                    </div>
                  }
                >
                  <span
                    style={{
                      color: "rgba(0,0,0,0.25)",
                      cursor: "not-allowed",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    {(it as any).icon && (
                      <i
                        className={(it as any).icon.props.className}
                        style={{ opacity: 0.3 }}
                      />
                    )}
                    {(it as any).label}
                  </span>
                </Tooltip>
              ),
            } as NonNullable<MenuProps["items"]>[number];
          }

          return it;
        });
      }

      return filtered;
    }
    if (record.Status === StatusEnum.Active) {
      if (record.isBilled) {
        return items.filter((it) => String((it as any).key) === "view");
      }
      const allow = new Set(["view", "edit", "cancel"]);
      if (record.isBilled === false) allow.add("pay");
      let filtered =
        items.filter((it) => allow.has(String((it as any).key))) ?? [];

      if (diffInHours <= 1) {
        filtered = (filtered ?? []).map((it) => {
          const key = String((it as any).key);
          if (["edit", "cancel"].includes(key)) {
            return {
              ...(it as any),
              icon: false,
              disabled: true,
              label: (
                <Tooltip
                  placement="left"
                  title={
                    <div>
                      <strong>
                        {key === "edit" ? "Editing" : "Cancellation"} is
                        unavailable.
                      </strong>
                      <div>
                        This campaign is locked as it gets closer to the
                        scheduled send time.
                      </div>
                    </div>
                  }
                >
                  <span
                    style={{
                      color: "rgba(0,0,0,0.25)",
                      cursor: "not-allowed",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    {(it as any).icon && (
                      <i
                        className={(it as any).icon.props.className}
                        style={{ opacity: 0.3 }}
                      />
                    )}
                    {(it as any).label}
                  </span>
                </Tooltip>
              ),
            } as NonNullable<MenuProps["items"]>[number];
          }
          return it;
        });
      }

      return filtered;
    }
  }
  return items;
};

const ACTION_ITEMS: MenuProps["items"] = [
  {
    key: "view",
    icon: <i className="icon-view-black" />,
    label: "View Detail",
  },
  // {
  //   key: "pay",
  //   icon: <i className="icon-payment-black" />,
  //   label: "Complete Payment",
  // },
  {
    key: "edit",
    icon: <i className="icon-edit-black" />,
    label: "Edit",
  },
  { key: "cancel", icon: <i className="icon-cancel-black" />, label: "Cancel" },
  {
    key: "activate",
    icon: <i className="icon-activate-black" />,
    label: "Activate Campaign",
  },
  // { key: "pause", icon: <i className="icon-pause-black" />, label: "Pause" },
  // { key: "stop", icon: <i className="icon-stop-black" />, label: "Stop" },
  // { key: "resume", icon: <i className="icon-resume-black" />, label: "Resume" },
  {
    key: "delete",
    icon: <i className="icon-delete-black" />,
    label: "Delete",
  },
];
const { Title, Text } = Typography;

export default function HistoryCampaignCardContent() {
  const [search, setSearch] = useState<string>("");
  const [searchMobile, setSearchMobile] = useState<string>("");

  const [statuses, setStatuses] = useState<string[]>([]);
  const [channels, setChannels] = useState<string[]>([]);
  const [segments, setSegments] = useState<string[]>([]);

  const [statusesMobile, setStatusesMobile] = useState<string[]>([]);
  const [channelsMobile, setChannelsMobile] = useState<string[]>([]);
  const [segmentsMobile, setSegmentsMobile] = useState<string[]>([]);

  const [listCampaign, setListCampaign] = useState<Campaign[] | null>(null);

  const [start, setStart] = useState<Dayjs | null>(null);
  const [end, setEnd] = useState<Dayjs | null>(null);
  const [startMobile, setStartMobile] = useState<Dayjs | null>(null);
  const [endMobile, setEndMobile] = useState<Dayjs | null>(null);
  const endRef = useRef<any>(null);

  const { brandId } = useBrandStore();
  const [showTimeout, setShowTimeout] = useState(false);
  const [messageTimeoutModal, setMessageTimeoutModal] = useState(
    "This campaign is approaching its scheduled send time, so editing is no longer available",
  );
  const [titleTimeoutModal, setTitleTimeoutModal] = useState(
    "Editing Unavailable",
  );
  const [variant, setVariant] = useState<Variant | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(
    null,
  );
  const [actionType, setActionType] = useState<string | null>(null);

  const [
    isModalSuccessActivateCampaignVisible,
    setIsModalSuccessActivateCampaignVisible,
  ] = useState(false);
  const [
    isModalSuccessDeleteCampaignVisible,
    setIsModalSuccessDeleteCampaignVisible,
  ] = useState(false);
  const [loading, setLoading] = useState(false);

  // Billing flow
  const [billingModalOpen, setBillingModalOpen] = useState(false);
  const [billingSource, setBillingSource] = useState<"activate" | "pay">(
    "activate",
  );
  const [billingCampaign, setBillingCampaign] = useState<Campaign | null>(null);
  const [billingTotalFee, setBillingTotalFee] = useState<number | undefined>(
    undefined,
  );
  const [billingMemberCount, setBillingMemberCount] = useState(0);
  const [paymentSuccessModalOpen, setPaymentSuccessModalOpen] = useState(false);
  const [paymentFailedModalOpen, setPaymentFailedModalOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const navigateWithQuery = useQueryNavigator();

  const closeModal = useCallback(() => {
    // if (variant === "activate") {
    //   navigateWithQuery(RoutePaths.IFRAME_DETAIL_CAMPAIGN, "id", selectedId);
    // }

    setVariant(null);
    setSelectedId(null);
  }, [variant]);

  const handleConfirm = useCallback(async () => {
    if (!variant) return;

    try {
      setLoading(true);

      if (variant === "activate") {
        // setBillingCampaign(selectedCampaign);
        // setBillingTotalFee(undefined);
        // setBillingMemberCount(selectedCampaign?.EstimatedTotalMembers ?? 0);
        // setBillingSource("activate");
        // setBillingModalOpen(true);

        const result = await campaignService.updateCampaignDetailsStatus(
          selectedId ?? 0,
          StatusEnum.Active,
        );

        if (result === 1) {
          setTitleTimeoutModal("Activate Success");

          setActionType("activate");
          setIsModalSuccessActivateCampaignVisible(true);
          await fetchHistoryCampaign();
        }
        if (result === null) {
          setTitleTimeoutModal("Activate Unavailable");
          setMessageTimeoutModal(
            "The campaign is locked as it gets closer to the scheduled send time.",
          );
          setShowTimeout(true);
          return;
        }
        return;
      }

      if (variant === "delete") {
        const result = await campaignService.deleteCampaignDetails(
          selectedId ?? 0,
        );
        setActionType("delete");
        setIsModalSuccessDeleteCampaignVisible(true);
        await fetchHistoryCampaign();
      }

      if (variant === "cancel") {
        const result = await campaignService.updateCampaignDetailsStatus(
          selectedId ?? 0,
          StatusEnum.Cancelled,
        );

        if (result === 1) {
          setActionType("cancel");
          setIsModalSuccessDeleteCampaignVisible(true);
          await fetchHistoryCampaign();
        }
        if (result === null) {
          setTitleTimeoutModal("Cancelling Unavailable");
          setMessageTimeoutModal(
            "The campaign is locked as it gets closer to the scheduled send time.",
          );
          setShowTimeout(true);
          return;
        }
      }
    } catch (error) {
      console.error("Error during handleConfirm:", error);
    } finally {
      setLoading(false);
      setVariant(null);
      setSelectedId(null);
    }
  }, [variant, selectedCampaign]);

  function useHandleAction() {
    return useCallback((key: ActionKey, record: any) => {
      switch (key) {
        case "view":
          setSelectedId(record.Id);
          navigate(
            `${RoutePaths.IFRAME_DETAIL_CAMPAIGN}${location.search}&id=${record.Id}`,
          );
          break;
        case "edit":
          setSelectedId(record.Id);

          navigateWithQuery(RoutePaths.IFRAME_UPDATE_CAMPAIGN, "id", record.Id);
          break;
        case "cancel":
          setSelectedId(record.Id);

          setVariant("cancel");
          break;
        case "activate":
          setSelectedId(record.Id);
          setSelectedCampaign(record);
          setVariant("activate");
          break;
          // case "pause":
          //   setVariant("pause");
          //   break;
          // case "stop":
          //   setVariant("stop");
          //   break;
          // case "resume":
          //   setVariant("resume");
          break;
        case "delete":
          setSelectedId(record.Id);
          setVariant("delete");
          break;
        case "pay": {
          const logs: any[] = (record.CampaignRunLog ?? [])
            .slice()
            .sort(
              (a: any, b: any) =>
                new Date(b.CreatedDate ?? 0).getTime() -
                new Date(a.CreatedDate ?? 0).getTime(),
            );
          const latestLog = logs[0];
          const fee = (latestLog?.MemberLogs ?? []).reduce(
            (sum: number, log: any) => sum + (log.Fee || 0) * (log.Count || 1),
            0,
          );
          setSelectedCampaign(record);
          setBillingCampaign(record);
          setBillingTotalFee(fee);
          setBillingMemberCount(
            latestLog?.TotalMembers ?? latestLog?.SuccessCount ?? 0,
          );
          setBillingSource("pay");
          setBillingModalOpen(true);
          break;
        }
      }
    }, []);
  }

  const filtered = useMemo(() => {
    if (!listCampaign) return [];

    return listCampaign?.filter((r) => {
      const textOk =
        !search || r?.Name.toLowerCase().includes(search?.toLowerCase());

      const statusOk =
        statuses.length === 0 || statuses.includes(StatusKeyMap[r.Status]);
      const channelOk =
        channels.length === 0 || channels.includes(ChannelKeyMap[r.Channel]);

      const segmentOk =
        !segments?.length ||
        segments.some((s) =>
          r.Segments?.some((seg) => seg.SegmentId === Number(s)),
        );

      const dateOk = (() => {
        if (!start && !end) return true;

        const inRange = (val?: Date) => {
          if (!val) return false;

          const d = dayjs(val);
          const s = start ? start.startOf("day") : null;
          const e = end ? end.endOf("day") : null;

          if (s && d.isBefore(s)) return false;
          if (e && d.isAfter(e)) return false;
          return true;
        };

        return inRange(r.CreatedDate);
      })();

      return textOk && statusOk && channelOk && segmentOk && dateOk;
    });
  }, [listCampaign, search, statuses, channels, segments, start, end]);

  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const paginatedData = filtered?.slice(
    (current - 1) * pageSize,
    current * pageSize,
  );
  const handleAction = useHandleAction();

  const columns: ColumnsType<Campaign> = [
    {
      title: "Created Date",
      dataIndex: "CreatedDate",
      key: "createdDate",
      render: (value: Date) =>
        dayjs(value).add(-7, "hour").format("YYYY-MM-DD"),
    },
    {
      title: "Campaign Name",
      dataIndex: "Name",
      key: "name",
      width: 350,
      render: (t) => (
        <span style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
          {t}
        </span>
      ),
    },
    {
      title: "Payment Status",
      key: "paymentStatus",
      width: 50,
      render: (_: any, record: Campaign) => {
        const logs = record.CampaignRunLog || [];

        const latestLog = logs
          .slice()
          .sort(
            (a, b) =>
              new Date(b.CreatedDate ?? 0).getTime() -
              new Date(a.CreatedDate ?? 0).getTime(),
          )[0];

        const isBilled = latestLog?.IsBilled;

        const color = isBilled ? "green" : "red";
        const text = isBilled ? "Paid" : "Unpaid";

        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: "Channel",
      dataIndex: "Channel",
      key: "channel",
      render: (c: ChannelEnum) => (
        <Space>
          {channelIcon(c)}
          {ChannelKeyMap[c]}
        </Space>
      ),
    },
    {
      title: "Type",
      dataIndex: "ScheduleType",
      key: "type",
      render: (c: TypeEnum) => <Space>{TypeKeyMap[c]}</Space>,
    },
    {
      title: "Status",
      dataIndex: "Status",
      key: "status",
      render: statusTag,
    },
    {
      title: "Campaign Date",
      dataIndex: "CampaignStart",
      key: "CampaignStart",
      render: (v?: string) => (v ? dayjs(v).format("YYYY-MM-DD HH:mm") : "-"),
    },

    // {
    //   title: "Next Schedule",
    //   dataIndex: "nextSchedule",
    //   key: "nextSchedule",
    //   width: 170,
    //   render: (v?: string) => (v ? v : "-"),
    // },
    {
      title: "Segment",
      dataIndex: "Segments",
      key: "segments",
      render: (segs: Segment[]) => {
        const names = Array.from(new Set(segs.map((s) => s.SegmentName)));

        return <SegmentCell segs={names} />;
      },
    },

    {
      title: "Criteria Profile",
      dataIndex: "RFMProfile",
      key: "criteriaProfile",
      render: (id?: number | string) => {
        if (id === undefined || id === null) return "-";
        const name = profileNameMap.get(Number(id));
        return name ?? `#${id}`;
      },
    },

    {
      title: "Action",
      key: "action",
      fixed: "right",
      width: 60,
      render: (_: unknown, record: Campaign) => {
        const logs = (record.CampaignRunLog ?? [])
          .slice()
          .sort(
            (a, b) =>
              new Date(b.CreatedDate ?? 0).getTime() -
              new Date(a.CreatedDate ?? 0).getTime(),
          );
        const isBilled = logs[0]?.IsBilled;
        const filteredItems = getActionMenuItems({ ...record, isBilled });
        return (
          <Dropdown
            menu={{
              items: filteredItems,
              onClick: ({ key }) => handleAction(key as ActionKey, record),
            }}
            trigger={["click"]}
          >
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  const disabledStartDate = (current: Dayjs) => {
    if (!end) return false;
    return current.isAfter(end, "day");
  };

  const disabledEndDate = (current: Dayjs) => {
    if (!start) return false;
    return current.isBefore(start, "day");
  };

  const fetchHistoryCampaign = async () => {
    try {
      setLoading(true);
      if (!brandId) return;

      const response = await campaignService.getAllCampaignDetails(brandId);
      setListCampaign(response);
    } catch (error) {
      console.error("Error fetching history campaign:", error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    const fetchData = async () => {
      await Promise.all([fetchHistoryCampaign()]);
    };

    fetchData();
  }, []);

  const [listBrandMemberSegment, setListBrandMemberSegment] = useState<
    { Id: number; Name: string }[]
  >([]);
  const [listBrandRFMProfile, setListBrandRFMProfile] = useState<
    { Id: number; Name: string }[]
  >([]);
  const realSegmentOptions = listBrandMemberSegment.map((s) => ({
    label: s.Name,
    value: String(s.Id),
  }));

  const ALL_VALUE = "All";

  const segmentOptionsWithAll = [
    { label: "All Segment", value: ALL_VALUE },
    ...realSegmentOptions,
  ];
  const channelOptionsWithAll = [
    { label: "All Channel", value: ALL_VALUE },
    ...CHANNEL_OPTIONS,
  ];

  const statusesOptionsWithAll = [
    { label: "All Status", value: ALL_VALUE },
    ...STATUS_OPTIONS,
  ];

  const profileNameMap = useMemo(
    () => new Map(listBrandRFMProfile.map((s) => [s.Id, s.Name])),
    [listBrandRFMProfile],
  );
  const fetchBrandMemberSegment = async () => {
    if (brandId) {
      try {
        const data =
          await brandMemberSegmentService.listBrandMemberSegment(brandId);
        if (data) {
          setListBrandMemberSegment(data);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
      }
    }
  };

  const fetchBrandRFMProfile = async () => {
    if (brandId) {
      try {
        const data = await brandRFMProfileService.listBrandRFMProfile(brandId);
        if (data) {
          setListBrandRFMProfile(data);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
      }
    }
  };
  useEffect(() => {
    const timeout = setTimeout(async () => {
      if (brandId) {
        await fetchBrandMemberSegment();
        await fetchBrandRFMProfile();
      }
    }, 100);
    const fetchSettings = async () => {};
    fetchSettings();

    return () => clearTimeout(timeout);
  }, [brandId]);
  const isMobile = window.innerWidth < 768;
  const [isModalFilterVisible, setIsModalFilterVisible] = useState(false);
  const applyFilterHistoryCampaign = () => {
    setSearch(searchMobile);
    setStart(startMobile);
    setEnd(endMobile);
    setStatuses(statusesMobile);
    setChannels(channelsMobile);
    setSegments(segmentsMobile);
    setIsModalFilterVisible(false);
  };

  const cancelFilterHistoryCampaign = () => {
    setSearchMobile(search);
    setStartMobile(start);
    setEndMobile(end);
    setStatusesMobile(statuses);
    setChannelsMobile(channels);
    setSegmentsMobile(segments);
    setIsModalFilterVisible(false);
  };

  useEffect(() => {
    setChannels([...channelOptionsWithAll.map((o) => o.value)]);
    setSegments([...segmentOptionsWithAll.map((o) => o.value)]);
    setStatuses([...statusesOptionsWithAll.map((o) => o.value)]);
  }, [listBrandMemberSegment, listBrandRFMProfile]);

  useEffect(() => {
    setCurrent(1);
  }, [search, statuses, channels, segments, start, end]);

  return (
    <>
      <Form layout="vertical">
        {isMobile && (
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col span={24}>
              <Button
                color="default"
                variant="outlined"
                icon={<FilterOutlined />}
                style={{ width: "100%" }}
                onClick={() => setIsModalFilterVisible(true)}
              >
                Filter
              </Button>
            </Col>
          </Row>
        )}
        {!isMobile && (
          <Row gutter={[12, 12]}>
            <Col xs={24} md={8} lg={4}>
              <Form.Item label="Search" className="label-filter">
                <Input
                  allowClear
                  prefix={<SearchOutlined />}
                  placeholder="Search Campaign"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={8} lg={4}>
              <Form.Item label="From Date" className="label-filter">
                <DatePicker
                  style={{ width: "100%", height: "unset !important" }}
                  value={start}
                  format="YYYY-MM-DD"
                  placeholder="YYYY - MM - DD"
                  disabledDate={disabledStartDate}
                  onChange={(val) => {
                    setStart(val);
                    setTimeout(() => endRef.current?.focus(), 0);
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8} lg={4}>
              <Form.Item label="To Date" className="label-filter">
                <DatePicker
                  style={{ width: "100%", height: "unset !important" }}
                  value={end}
                  format="YYYY-MM-DD"
                  placeholder="YYYY - MM - DD"
                  disabledDate={disabledEndDate}
                  onChange={(val) => setEnd(val)}
                  ref={endRef}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={8} lg={4}>
              <Form.Item label="Status" className="label-filter">
                <Select<string[]>
                  mode="multiple"
                  allowClear
                  style={{ width: "100%" }}
                  value={statuses}
                  onChange={(v) =>
                    handleSelectAll(v, statuses, STATUS_OPTIONS, setStatuses)
                  }
                  // onChange={(v) => setStatuses(v)}
                  placeholder="All Status"
                  options={statusesOptionsWithAll}
                  showSearch={false}
                  filterOption={false}
                  maxTagCount={0}
                  maxTagPlaceholder={() =>
                    statuses.includes(ALL_VALUE)
                      ? "All Status"
                      : `${statuses.length}/${STATUS_OPTIONS.length} selected`
                  }
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={8} lg={4}>
              <Form.Item label="Channel" className="label-filter">
                <Select<string[]>
                  showSearch={false}
                  filterOption={false}
                  mode="multiple"
                  allowClear
                  style={{ width: "100%" }}
                  value={channels}
                  onChange={(v) =>
                    handleSelectAll(v, channels, CHANNEL_OPTIONS, setChannels)
                  }
                  placeholder="All Channel"
                  options={channelOptionsWithAll}
                  maxTagCount={0}
                  maxTagPlaceholder={() =>
                    channels.includes(ALL_VALUE)
                      ? "All Channel"
                      : `${channels.length}/${CHANNEL_OPTIONS.length} selected`
                  }
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={8} lg={4}>
              <Form.Item label="Segment" className="label-filter">
                <Select<string[]>
                  showSearch={false}
                  filterOption={false}
                  mode="multiple"
                  allowClear
                  maxTagCount={0}
                  maxTagPlaceholder={() =>
                    segments.includes(ALL_VALUE)
                      ? "All Segment"
                      : `${segments.length}/${realSegmentOptions.length} selected`
                  }
                  style={{ width: "100%" }}
                  value={segments}
                  onChange={(v) =>
                    handleSelectAll(
                      v,
                      segments,
                      realSegmentOptions,
                      setSegments,
                    )
                  }
                  // onChange={(v) => setSegments(v)}
                  placeholder="All Segment"
                  options={segmentOptionsWithAll}
                />
              </Form.Item>
            </Col>
          </Row>
        )}
      </Form>

      <Table
        rowKey="key"
        columns={columns}
        dataSource={paginatedData}
        bordered={false}
        size="middle"
        pagination={false}
        loading={loading}
        scroll={{ x: "max-content" }}
      />
      <Row
        gutter={[12, 12]}
        justify={isMobileOrTablet ? "center" : "space-between"}
        style={{
          marginTop: 16,
          textAlign: isMobileOrTablet ? "center" : "unset",
        }}
      >
        <Col xs={24} xl={12} md={12}>
          Results per Page{" "}
          <Select
            value={pageSize}
            style={{ width: 80 }}
            onChange={(value) => {
              setPageSize(value);
              setCurrent(1);
            }}
            options={[
              { value: 10, label: "10" },
              { value: 20, label: "20" },
              { value: 50, label: "50" },
              { value: 100, label: "100" },
            ]}
          />{" "}
          of {filtered?.length} items
        </Col>

        <Col xs={24} xl={12} md={12}>
          <Pagination
            style={{
              display: "inline-flex",
              float: isMobileOrTablet ? undefined : "inline-end",
            }}
            current={current}
            pageSize={pageSize}
            total={filtered?.length}
            showSizeChanger={false}
            onChange={(page) => setCurrent(page)}
          />
        </Col>
      </Row>
      {variant && (
        <CampaignAlertModal
          open
          variant={variant}
          onCancel={closeModal}
          onConfirm={handleConfirm}
          confirmLoading={loading}
        />
      )}
      <Modal
        title="Filter"
        className="filter-modal"
        open={isModalFilterVisible}
        style={{ bottom: 0 }}
        footer={null}
        transitionName=""
        maskTransitionName=""
        maskClosable={false}
        onCancel={cancelFilterHistoryCampaign}
        modalRender={(modal) => (
          <div style={{ position: "fixed", bottom: 0, width: "100%" }}>
            {modal}
          </div>
        )}
      >
        <Form layout="vertical">
          <Row>
            <Col xs={24} md={8} lg={4}>
              <Form.Item label="Search" className="label-filter">
                <Input
                  allowClear
                  prefix={<SearchOutlined />}
                  placeholder="Search Campaign"
                  value={search}
                  onChange={(e) => setSearchMobile(e.target.value)}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={10} lg={4}>
              <Form.Item label="From Date" className="label-filter">
                <DatePicker
                  style={{ width: "100%", height: "unset !important" }}
                  value={startMobile}
                  format="YYYY-MM-DD"
                  placeholder="YYYY - MM - DD"
                  disabledDate={disabledStartDate}
                  onChange={(val) => {
                    setStartMobile(val);
                    setTimeout(() => endRef.current?.focus(), 0);
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={10} lg={4}>
              <Form.Item label="To Date" className="label-filter">
                <DatePicker
                  style={{ width: "100%", height: "unset !important" }}
                  value={endMobile}
                  format="YYYY-MM-DD"
                  placeholder="YYYY - MM - DD"
                  disabledDate={disabledEndDate}
                  onChange={(val) => setEnd(val)}
                  ref={endRef}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={6} lg={4}>
              <Form.Item label="Status" className="label-filter">
                <Select<string[]>
                  mode="multiple"
                  allowClear
                  maxTagCount="responsive"
                  style={{ width: "100%" }}
                  value={statusesMobile}
                  onChange={(v) => setStatusesMobile(v)}
                  placeholder="All Status"
                  options={STATUS_OPTIONS}
                  showSearch={false}
                  filterOption={false}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={8} lg={4}>
              <Form.Item label="Channel" className="label-filter">
                <Select<string[]>
                  showSearch={false}
                  filterOption={false}
                  mode="multiple"
                  allowClear
                  maxTagCount="responsive"
                  style={{ width: "100%" }}
                  value={channelsMobile}
                  onChange={(v) => setChannelsMobile(v)}
                  placeholder="All Channel"
                  options={CHANNEL_OPTIONS}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={16} lg={4}>
              <Form.Item label="Segment" className="label-filter">
                <Select<string[]>
                  showSearch={false}
                  filterOption={false}
                  mode="multiple"
                  allowClear
                  style={{ width: "100%" }}
                  value={segmentsMobile}
                  maxTagCount={0}
                  maxTagPlaceholder={() =>
                    segments.includes(ALL_VALUE)
                      ? "All Segment"
                      : `${segments.length}/${realSegmentOptions.length} selected`
                  }
                  onChange={(v) => setSegmentsMobile(v)}
                  placeholder="All Segment"
                  options={segmentOptionsWithAll}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Button
              type="primary"
              style={{ width: "100%" }}
              onClick={applyFilterHistoryCampaign}
            >
              Apply Filter
            </Button>
          </Row>
        </Form>
      </Modal>
      {/* Activate success modal — shown after "Payment Later" from billing */}
      <Modal
        open={isModalSuccessActivateCampaignVisible}
        onCancel={() => {
          setIsModalSuccessActivateCampaignVisible(false);
          setSelectedId(null);
        }}
        footer={null}
        centered
        width={520}
        closable={true}
        maskClosable={false}
        styles={{ body: { paddingTop: 28, paddingBottom: 24 } }}
      >
        <Space
          direction="vertical"
          align="center"
          style={{ width: "100%", textAlign: "center" }}
          size={16}
        >
          <CheckCircleFilled style={{ fontSize: 80, color: "#2ecc71" }} />
          <Title level={4} style={{ margin: 0, textAlign: "center" }}>
            Campaign Activated Successfully
          </Title>
          <Text type="secondary" style={{ textAlign: "center" }}>
            Your campaign is set to be sent on{" "}
            {dayjs(selectedCampaign?.CampaignStart).format("YYYY-MM-DD HH:mm")}.
            You can view or edit it from the campaign history.
          </Text>
        </Space>
        <Divider style={{ margin: 5 }} />
        <Row>
          <Button
            type="primary"
            size="large"
            className="w-100"
            onClick={() => {
              navigate(
                `${RoutePaths.IFRAME_DETAIL_CAMPAIGN}${location.search}&id=${selectedCampaign?.Id}`,
              );
            }}
            style={{ marginTop: 8 }}
          >
            View Details
          </Button>
        </Row>
      </Modal>

      {/* Billing modal */}
      <BillingModal
        open={billingModalOpen}
        campaignId={billingCampaign?.Id ?? 0}
        campaignRunLogId={
          billingCampaign?.CampaignRunLog?.reduce(
            (latest, current) => {
              if (!latest) return current;

              const currentTime = current.CreatedDate?.getTime() ?? -Infinity;
              const latestTime = latest.CreatedDate?.getTime() ?? -Infinity;

              return currentTime > latestTime ? current : latest;
            },
            undefined as
              | (typeof billingCampaign.CampaignRunLog)[number]
              | undefined,
          )?.Id ?? 0
        }
        maxBudget={billingCampaign?.Budget}
        campaignName={billingCampaign?.Name ?? ""}
        channel={billingCampaign?.Channel ?? null}
        estimatedMembers={billingMemberCount}
        costPerMessage={getChannelCost(billingCampaign?.Channel ?? null)}
        totalFee={billingTotalFee}
        campaignStart={billingCampaign?.CampaignStart}
        campaignEnd={billingCampaign?.CampaignEnd}
        onClose={() => setBillingModalOpen(false)}
        mode={billingCampaign?.Mode}
        hidePayLater={billingSource === "pay"}
        onConfirmPayment={
          billingSource === "activate"
            ? async (_email) => {
                try {
                  setLoading(true);
                  await campaignService.updateCampaignDetailsStatus(
                    billingCampaign?.Id ?? 0,
                    StatusEnum.Active,
                    true,
                  );
                } finally {
                  setLoading(false);
                }
              }
            : undefined
        }
        onPaymentLater={async () => {
          setBillingModalOpen(false);
          if (billingSource === "activate") {
            await campaignService.updateCampaignDetailsStatus(
              billingCampaign?.Id ?? 0,
              StatusEnum.Active,
              false,
            );
            setIsModalSuccessActivateCampaignVisible(true);
          }
        }}
        onSuccess={() => {
          setBillingModalOpen(false);
          fetchHistoryCampaign();
          setPaymentSuccessModalOpen(true);
        }}
        onFailed={() => {
          setBillingModalOpen(false);
          setPaymentFailedModalOpen(true);
        }}
      />

      {/* Payment success modal */}
      <Modal
        open={paymentSuccessModalOpen}
        onCancel={() => setPaymentSuccessModalOpen(false)}
        footer={null}
        centered
        width={520}
        closable={false}
        maskClosable={false}
        styles={{ body: { paddingTop: 28, paddingBottom: 24 } }}
      >
        <Space
          direction="vertical"
          align="center"
          style={{ width: "100%", textAlign: "center" }}
          size={16}
        >
          <CheckCircleFilled style={{ fontSize: 80, color: "#2ecc71" }} />
          <Title level={4} style={{ margin: 0 }}>
            {billingSource === "activate"
              ? "Campaign Activated & Payment Successful"
              : "Payment Successful"}
          </Title>
          <Text type="secondary">
            Payment for <b>{billingCampaign?.Name}</b> has been processed
            successfully.
          </Text>
        </Space>
        <Divider style={{ margin: "16px 0 8px" }} />
        <Button
          type="primary"
          size="large"
          className="w-100"
          onClick={() => {
            setPaymentSuccessModalOpen(false);
            navigate(
              `${RoutePaths.IFRAME_DETAIL_CAMPAIGN}${location.search}&id=${billingCampaign?.Id}`,
            );
          }}
          style={{ marginTop: 8 }}
        >
          View Details
        </Button>
      </Modal>

      {/* Payment failed modal */}
      <Modal
        open={paymentFailedModalOpen}
        onCancel={() => setPaymentFailedModalOpen(false)}
        footer={null}
        centered
        width={520}
        closable={false}
        maskClosable={false}
        styles={{ body: { paddingTop: 28, paddingBottom: 24 } }}
      >
        <Space
          direction="vertical"
          align="center"
          style={{ width: "100%", textAlign: "center" }}
          size={16}
        >
          <ExclamationCircleFilled style={{ fontSize: 80, color: "#ff4d4f" }} />
          <Title level={4} style={{ margin: 0 }}>
            Payment Failed
          </Title>
          <Text type="secondary">
            {billingSource === "activate"
              ? "Your campaign was activated but payment could not be processed."
              : "Payment could not be processed."}{" "}
            You can retry payment from the campaign detail page.
          </Text>
        </Space>
        <Divider style={{ margin: "16px 0 8px" }} />
        <Row gutter={[12, 12]}>
          <Col span={12}>
            <Button
              size="large"
              className="w-100"
              onClick={async () => {
                setPaymentFailedModalOpen(false);
                setBillingModalOpen(false);
                if (billingSource === "activate") {
                  setLoading(true);
                  try {
                    await campaignService.updateCampaignDetailsStatus(
                      billingCampaign?.Id ?? 0,
                      StatusEnum.Active,
                      false,
                    );
                    setIsModalSuccessActivateCampaignVisible(true);
                  } finally {
                    setLoading(false);
                  }
                }
              }}
              style={{ marginTop: 8 }}
            >
              Set Up Payment Later
            </Button>
          </Col>
          <Col span={12}>
            <Button
              type="primary"
              size="large"
              className="w-100"
              onClick={() => {
                setPaymentFailedModalOpen(false);
                if (billingSource === "activate") {
                  setIsModalSuccessActivateCampaignVisible(true);
                } else {
                  setBillingModalOpen(true);
                }
              }}
              style={{ marginTop: 8 }}
            >
              Retry Payment
            </Button>
          </Col>
        </Row>
      </Modal>
      <Modal
        open={isModalSuccessDeleteCampaignVisible}
        onCancel={() => {
          setIsModalSuccessDeleteCampaignVisible(false);
        }}
        footer={null}
        centered
        width={520}
        closable={true}
        maskClosable={false}
        styles={{ body: { paddingTop: 28 } }}
      >
        <Space
          direction="vertical"
          align="center"
          style={{ width: "100%", textAlign: "center" }}
          size={16}
        >
          <CheckCircleFilled style={{ fontSize: 80, color: "#2ecc71" }} />
          <Title level={4} style={{ margin: 0, textAlign: "center" }}>
            Campaign {actionType === "delete" ? "Deleted" : "Canceled"}
          </Title>
          <Text type="secondary" style={{ textAlign: "center" }}>
            The Campaign has been{" "}
            {actionType === "delete"
              ? "permanently deleted"
              : "successfully canceled"}
          </Text>
        </Space>
        <Divider style={{ margin: 5 }} />
        <Row>
          <Button
            type="primary"
            size="large"
            className="w-100"
            onClick={() => {
              setIsModalSuccessDeleteCampaignVisible(false);
            }}
            style={{ marginTop: 8 }}
          >
            Ok
          </Button>
        </Row>
      </Modal>
      <CustomModal
        type="timeout"
        title={titleTimeoutModal}
        message={messageTimeoutModal}
        isVisible={showTimeout}
        onClose={() => setShowTimeout(false)}
        autoClose={false}
      />
    </>
  );
}
